import React, { useState, useEffect, useCallback } from 'react';
import { Save, Truck, Loader2, MessageSquare } from 'lucide-react';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Toaster, toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const { token, user } = useAuth();
  const [settings, setSettings] = useState({
    site_name: 'Kre8ing Your IdeaS',
    phone: '443-940-6660',
    email: 'info@shopkyis.com',
    address: '129 Slade Ave, Pikesville, MD 21208',
    hours: 'Mon - Fri: 10am - 6pm',
    facebook: 'https://web.facebook.com/kre8ingyourideas',
    instagram: 'https://www.instagram.com/kre8ing_your_ideas/',
    tax_rate: '6',
    free_shipping_threshold: '75',
  });
  const [chatSnippet, setChatSnippet] = useState('');
  const [chatSaving, setChatSaving] = useState(false);
  const [chatLoading, setChatLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  // Load existing chat widget snippet from the DB on mount.
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/chat-widget`);
        setChatSnippet(res.data?.snippet || '');
      } catch {
        // Non-fatal — settings page still usable.
      } finally {
        setChatLoading(false);
      }
    })();
  }, []);

  const handleSaveChatWidget = async () => {
    setChatSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/settings/chat_widget_snippet`,
        { value: chatSnippet || '', value_type: 'html' },
        { headers }
      );
      toast.success('Chat widget saved. Reload any tab to see it.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save chat widget');
    } finally {
      setChatSaving(false);
    }
  };

  const handleChange = (e) => setSettings({ ...settings, [e.target.name]: e.target.value });

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/settings`, settings, { headers });
      toast.success('Settings saved');
    } catch {
      toast.info('Settings saved locally (backend endpoint coming soon)');
    }
    setSaving(false);
  };

  const inputCls = "w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4] text-gray-900 text-sm";
  const labelCls = "block text-sm font-medium mb-1 text-gray-700";

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div className="mb-8" data-testid="admin-settings-page">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your site configuration</p>
      </div>

      <form onSubmit={handleSave} className="space-y-8">
        {/* General */}
        <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <h2 className="text-lg font-bold mb-4 text-gray-900">General</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Site Name</label>
              <input type="text" name="site_name" value={settings.site_name} onChange={handleChange} className={inputCls} data-testid="setting-site-name" />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input type="text" name="phone" value={settings.phone} onChange={handleChange} className={inputCls} data-testid="setting-phone" />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" name="email" value={settings.email} onChange={handleChange} className={inputCls} data-testid="setting-email" />
            </div>
            <div>
              <label className={labelCls}>Address</label>
              <input type="text" name="address" value={settings.address} onChange={handleChange} className={inputCls} data-testid="setting-address" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Business Hours</label>
              <input type="text" name="hours" value={settings.hours} onChange={handleChange} className={inputCls} data-testid="setting-hours" />
            </div>
          </div>
        </div>

        {/* Social */}
        <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <h2 className="text-lg font-bold mb-4 text-gray-900">Social Media</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Facebook URL</label>
              <input type="url" name="facebook" value={settings.facebook} onChange={handleChange} className={inputCls} data-testid="setting-facebook" />
            </div>
            <div>
              <label className={labelCls}>Instagram URL</label>
              <input type="url" name="instagram" value={settings.instagram} onChange={handleChange} className={inputCls} data-testid="setting-instagram" />
            </div>
          </div>
        </div>

        {/* Chat Widget */}
        <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm" data-testid="chat-widget-section">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#4A90A4]/10 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-5 h-5 text-[#4A90A4]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Chat Widget</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Paste the embed code from your chat provider (Intercom, Tawk.to, Drift, Crisp, Facebook Messenger, etc.). It will render on every page of the site as soon as you save.
              </p>
            </div>
          </div>
          <label className={labelCls}>Embed code</label>
          <textarea
            rows={10}
            value={chatSnippet}
            onChange={(e) => setChatSnippet(e.target.value)}
            placeholder={'<!-- Paste your chat widget snippet here -->\n<script>\n  // e.g. Tawk.to, Intercom, Drift, Crisp, ...\n</script>'}
            className="w-full px-4 py-3 bg-gray-900 text-green-300 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4] font-mono text-xs leading-relaxed"
            data-testid="chat-widget-snippet"
            disabled={chatLoading}
          />
          <p className="text-xs text-gray-400 mt-2">
            Anything you paste here is injected as-is into the page. Only paste code from providers you trust.
          </p>
          <div className="flex items-center gap-2 mt-4">
            <button
              type="button"
              onClick={handleSaveChatWidget}
              disabled={chatSaving || chatLoading}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#F5A623] text-white text-sm font-bold rounded-lg hover:brightness-110 disabled:opacity-50 transition-all"
              data-testid="save-chat-widget-btn"
            >
              {chatSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Chat Widget</>}
            </button>
            {chatSnippet && (
              <button
                type="button"
                onClick={() => setChatSnippet('')}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-600 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-all"
                data-testid="clear-chat-widget-btn"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* E-Commerce */}
        <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <h2 className="text-lg font-bold mb-4 text-gray-900">E-Commerce</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Tax Rate (%)</label>
              <input type="number" step="0.01" name="tax_rate" value={settings.tax_rate} onChange={handleChange} className={inputCls} data-testid="setting-tax-rate" />
            </div>
            <div>
              <label className={labelCls}>Free Shipping Threshold ($)</label>
              <input type="number" step="0.01" name="free_shipping_threshold" value={settings.free_shipping_threshold} onChange={handleChange} className={inputCls} data-testid="setting-free-shipping" />
            </div>
          </div>
        </div>

        {/* Account */}
        <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <h2 className="text-lg font-bold mb-4 text-gray-900">Your Account</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Email</label>
              <p className="text-sm text-gray-600 px-4 py-2.5 bg-gray-50 rounded-xl">{user?.email}</p>
            </div>
            <div>
              <label className={labelCls}>Role</label>
              <p className="text-sm text-gray-600 px-4 py-2.5 bg-gray-50 rounded-xl capitalize">{user?.role}</p>
            </div>
          </div>
        </div>

        {/* Auto-Print Shipping Labels */}
        <AutoLabelSettings token={token} />

        <div className="flex justify-end">
          <button type="submit" disabled={saving}
            className="inline-flex items-center px-8 py-3 bg-[#F5A623] text-gray-900 font-bold text-sm rounded-full btn-glow disabled:opacity-50"
            data-testid="save-settings-btn"
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
};

export default AdminSettings;

/* ────────────────────────────────────────────────────────────
 * Auto-Print Shipping Labels — per-key settings backed by
 * /api/admin/settings/{key}. Loads once on mount; each Save
 * persists all four keys in parallel.
 * ──────────────────────────────────────────────────────────── */
const AUTO_LABEL_KEYS = {
  auto_label_enabled: { default: 'false', type: 'boolean' },
  auto_label_weight_mode: { default: 'fixed', type: 'string' },
  auto_label_weight_oz: { default: '12', type: 'number' },
  auto_label_per_unit_oz: { default: '6', type: 'number' },
  auto_label_service: { default: 'US-GA', type: 'string' },
  auto_label_package: { default: 'Package', type: 'string' },
};

const SERVICE_OPTIONS = [
  { value: 'US-GA', label: 'USPS Ground Advantage (2-5 days, cheapest)' },
  { value: 'US-PM', label: 'USPS Priority Mail (1-3 days)' },
  { value: 'US-FCP', label: 'USPS First-Class Package (≤ 13 oz)' },
  { value: 'US-EM', label: 'USPS Priority Mail Express (1-2 days)' },
];

const PACKAGE_OPTIONS = [
  'Package', 'Flat Rate Envelope', 'Flat Rate Padded Envelope',
  'Small Flat Rate Box', 'Flat Rate Box', 'Large Flat Rate Box',
];

const AutoLabelSettings = ({ token }) => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [vals, setVals] = useState(() => {
    const init = {};
    Object.entries(AUTO_LABEL_KEYS).forEach(([k, v]) => { init[k] = v.default; });
    return init;
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/settings`, { headers: { Authorization: `Bearer ${token}` } });
      const next = { ...vals };
      Object.keys(AUTO_LABEL_KEYS).forEach((k) => {
        if (res.data[k]?.value !== undefined) next[k] = res.data[k].value;
      });
      setVals(next);
    } catch {
      // first run: settings rows just don't exist yet
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await Promise.all(
        Object.entries(AUTO_LABEL_KEYS).map(([k, meta]) =>
          axios.put(
            `${API}/api/admin/settings/${k}`,
            { value: String(vals[k] ?? meta.default), value_type: meta.type },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      toast.success('Auto-label settings saved');
    } catch (err) {
      toast.error('Failed to save auto-label settings');
    } finally { setSaving(false); }
  };

  const enabled = String(vals.auto_label_enabled) === 'true';
  const isPerQty = vals.auto_label_weight_mode === 'per_quantity';

  return (
    <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm" data-testid="auto-label-settings">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-[#1B3A5C]" />
          <h2 className="text-lg font-bold text-gray-900">Auto-Print Shipping Labels</h2>
        </div>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
      </div>
      <p className="text-sm text-gray-500 mb-5">
        When ON, the system buys a USPS label the moment you flip an order to <strong>Ready for Pickup/Ship</strong>.
        Requires the Stamps.com env vars to be set. A failure does not block the status change; it shows a red "Label Failed" pill on the order and emails all admins.
      </p>

      {/* Master toggle */}
      <label className="flex items-center justify-between gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 cursor-pointer mb-5" data-testid="auto-label-toggle-row">
        <div>
          <p className="text-sm font-semibold text-gray-900">Enable auto-print</p>
          <p className="text-xs text-gray-500">Off by default. Turn on after Stamps.com is configured.</p>
        </div>
        <div className="relative inline-flex">
          <input type="checkbox" className="sr-only peer"
            checked={enabled}
            onChange={(e) => setVals({ ...vals, auto_label_enabled: e.target.checked ? 'true' : 'false' })}
            data-testid="auto-label-enabled-toggle" />
          <span className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-[#F5A623] transition-colors"></span>
          <span className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full peer-checked:translate-x-5 transition-transform shadow"></span>
        </div>
      </label>

      <div className={`grid sm:grid-cols-2 gap-5 transition-opacity ${enabled ? '' : 'opacity-60 pointer-events-none'}`}>
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">Weight calculation</label>
          <select value={vals.auto_label_weight_mode}
            onChange={(e) => setVals({ ...vals, auto_label_weight_mode: e.target.value })}
            className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4] text-gray-900 text-sm"
            data-testid="auto-label-weight-mode">
            <option value="fixed">Fixed weight per order</option>
            <option value="per_quantity">Per unit × quantity</option>
          </select>
        </div>
        {!isPerQty ? (
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Fixed weight (oz)</label>
            <input type="number" min="0.1" step="0.1" value={vals.auto_label_weight_oz}
              onChange={(e) => setVals({ ...vals, auto_label_weight_oz: e.target.value })}
              className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4] text-gray-900 text-sm"
              data-testid="auto-label-weight-oz" />
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Per-unit weight (oz)</label>
            <input type="number" min="0.1" step="0.1" value={vals.auto_label_per_unit_oz}
              onChange={(e) => setVals({ ...vals, auto_label_per_unit_oz: e.target.value })}
              className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4] text-gray-900 text-sm"
              data-testid="auto-label-per-unit-oz" />
            <p className="text-xs text-gray-500 mt-1">Multiplied by total order quantity. E.g. 6 oz × 12 tees = 72 oz package.</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">USPS Service</label>
          <select value={vals.auto_label_service}
            onChange={(e) => setVals({ ...vals, auto_label_service: e.target.value })}
            className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4] text-gray-900 text-sm"
            data-testid="auto-label-service">
            {SERVICE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">Package type</label>
          <select value={vals.auto_label_package}
            onChange={(e) => setVals({ ...vals, auto_label_package: e.target.value })}
            className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4] text-gray-900 text-sm"
            data-testid="auto-label-package">
            {PACKAGE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      <div className="flex justify-end mt-5">
        <button type="button" onClick={save} disabled={saving || loading}
          className="inline-flex items-center px-6 py-2.5 bg-[#1B3A5C] text-white font-bold text-sm rounded-full hover:bg-[#0F2A3D] disabled:opacity-50"
          data-testid="save-auto-label-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Auto-Label Config'}
        </button>
      </div>
    </div>
  );
};
